// ============================================
// src/components/maintenance/MaintenanceTaskList.js
// Mobile-First Task List View
// ============================================

import React, { useState } from 'react';

function MaintenanceTaskList({ 
  tasks, 
  categories, 
  vendors, 
  filters, 
  onFilterChange, 
  onTaskClick,
  onUpdateTask,
  onCompleteTask,
  onDeleteTask,
  isAdmin,
  isSuperAdmin 
}) {
  const [expandedTask, setExpandedTask] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Filter tasks based on search
  const filteredTasks = tasks.filter(task => {
    const query = searchQuery.toLowerCase();
    return (
      task.title?.toLowerCase().includes(query) ||
      task.description?.toLowerCase().includes(query) ||
      task.maintenance_categories?.name?.toLowerCase().includes(query) ||
      task.service_vendors?.name?.toLowerCase().includes(query)
    );
  });

  // Group tasks by status for better organization
  const groupedTasks = {
    overdue: filteredTasks.filter(t => t.isOverdue),
    today: filteredTasks.filter(t => {
      const taskDate = new Date(t.scheduled_date).toDateString();
      const today = new Date().toDateString();
      return taskDate === today && !t.isOverdue;
    }),
    upcoming: filteredTasks.filter(t => {
      const taskDate = new Date(t.scheduled_date);
      const today = new Date();
      return taskDate > today && t.status === 'scheduled';
    }),
    inProgress: filteredTasks.filter(t => t.status === 'in_progress'),
    completed: filteredTasks.filter(t => t.status === 'completed')
  };

  const handleQuickStatusUpdate = async (taskId, newStatus) => {
    await onUpdateTask(taskId, { status: newStatus });
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    
    return d.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      weekday: 'short'
    });
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'urgent': return '#dc3545';
      case 'high': return '#fd7e14';
      case 'medium': return '#ffc107';
      case 'low': return '#28a745';
      default: return '#6c757d';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return '#28a745';
      case 'in_progress': return '#17a2b8';
      case 'scheduled': return '#6c757d';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  };

  return (
    <div>
      {/* Search Bar - Mobile Optimized */}
      <div style={{
        background: 'white',
        padding: '15px',
        borderRadius: '10px',
        marginBottom: '15px',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
      }}>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            placeholder="🔍 Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 40px 12px 15px',
              border: '2px solid #e0e0e0',
              borderRadius: '25px',
              fontSize: '16px',
              outline: 'none'
            }}
          />
          <button
            onClick={() => setShowFilters(!showFilters)}
            style={{
              position: 'absolute',
              right: '5px',
              top: '50%',
              transform: 'translateY(-50%)',
              padding: '8px 12px',
              background: showFilters ? '#667eea' : '#f0f0f0',
              color: showFilters ? 'white' : '#666',
              border: 'none',
              borderRadius: '20px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            🎯 Filter
          </button>
        </div>

        {/* Expandable Filters - Mobile Optimized */}
        {showFilters && (
          <div style={{
            marginTop: '15px',
            paddingTop: '15px',
            borderTop: '1px solid #e0e0e0'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '10px'
            }}>
              <select
                value={filters.status}
                onChange={(e) => onFilterChange({...filters, status: e.target.value})}
                style={selectStyle}
              >
                <option value="all">All Status</option>
                <option value="scheduled">Scheduled</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>

              <select
                value={filters.priority}
                onChange={(e) => onFilterChange({...filters, priority: e.target.value})}
                style={selectStyle}
              >
                <option value="all">All Priority</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>

              <select
                value={filters.category}
                onChange={(e) => onFilterChange({...filters, category: e.target.value})}
                style={selectStyle}
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>
                    {cat.icon} {cat.name}
                  </option>
                ))}
              </select>

              <select
                value={filters.dateRange}
                onChange={(e) => onFilterChange({...filters, dateRange: e.target.value})}
                style={selectStyle}
              >
                <option value="all">All Dates</option>
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month">This Month</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Task Groups */}
      {Object.entries(groupedTasks).map(([group, groupTasks]) => {
        if (groupTasks.length === 0) return null;

        const groupConfig = {
          overdue: { label: '⚠️ Overdue', color: '#dc3545' },
          today: { label: '📅 Today', color: '#28a745' },
          upcoming: { label: '📆 Upcoming', color: '#17a2b8' },
          inProgress: { label: '🔄 In Progress', color: '#ffc107' },
          completed: { label: '✅ Completed', color: '#6c757d' }
        };

        return (
          <div key={group} style={{ marginBottom: '20px' }}>
            <h4 style={{
              margin: '0 0 10px 0',
              color: groupConfig[group].color,
              fontSize: '16px',
              fontWeight: '600'
            }}>
              {groupConfig[group].label} ({groupTasks.length})
            </h4>

            {groupTasks.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                isExpanded={expandedTask === task.id}
                onToggleExpand={() => setExpandedTask(
                  expandedTask === task.id ? null : task.id
                )}
                onTaskClick={onTaskClick}
                onQuickStatusUpdate={handleQuickStatusUpdate}
                onDelete={onDeleteTask}
                formatDate={formatDate}
                getPriorityColor={getPriorityColor}
                getStatusColor={getStatusColor}
                isAdmin={isAdmin}
                isSuperAdmin={isSuperAdmin}
              />
            ))}
          </div>
        );
      })}

      {filteredTasks.length === 0 && (
        <div style={{
          background: 'white',
          borderRadius: '10px',
          padding: '40px 20px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🔧</div>
          <h3 style={{ color: '#666', marginBottom: '10px' }}>No tasks found</h3>
          <p style={{ color: '#999' }}>
            {searchQuery ? 'Try adjusting your search' : 'Add your first maintenance task'}
          </p>
        </div>
      )}
    </div>
  );
}

// Task Card Component - Mobile Optimized
function TaskCard({ 
  task, 
  isExpanded, 
  onToggleExpand, 
  onTaskClick,
  onQuickStatusUpdate,
  onDelete,
  formatDate,
  getPriorityColor,
  getStatusColor,
  isAdmin,
  isSuperAdmin
}) {
  return (
    <div style={{
      background: 'white',
      borderRadius: '10px',
      marginBottom: '10px',
      boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
      overflow: 'hidden',
      border: task.isOverdue ? '2px solid #dc3545' : 'none'
    }}>
      {/* Main Card Content */}
      <div
        onClick={onToggleExpand}
        style={{
          padding: '15px',
          cursor: 'pointer'
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '10px'
        }}>
          <div style={{ flex: 1 }}>
            <h4 style={{
              margin: '0 0 5px 0',
              fontSize: '16px',
              fontWeight: '600',
              color: '#333'
            }}>
              {task.maintenance_categories?.icon} {task.title}
            </h4>
            {task.description && (
              <p style={{
                margin: '0 0 8px 0',
                fontSize: '14px',
                color: '#666',
                lineHeight: '1.4'
              }}>
                {task.description.substring(0, 100)}
                {task.description.length > 100 && '...'}
              </p>
            )}
          </div>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '5px'
          }}>
            <span style={{
              padding: '3px 8px',
              borderRadius: '12px',
              background: getPriorityColor(task.priority),
              color: 'white',
              fontSize: '11px',
              fontWeight: '600'
            }}>
              {task.priority?.toUpperCase()}
            </span>
            {task.task_type === 'recurring' && (
              <span style={{
                padding: '3px 8px',
                borderRadius: '12px',
                background: '#9b59b6',
                color: 'white',
                fontSize: '11px'
              }}>
                🔄 RECURRING
              </span>
            )}
          </div>
        </div>

        {/* Task Meta Info */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          fontSize: '13px',
          color: '#666'
        }}>
          <span>📅 {formatDate(task.scheduled_date)}</span>
          {task.scheduled_time && (
            <span>⏰ {task.scheduled_time}</span>
          )}
          {task.service_vendors && (
            <span>👷 {task.service_vendors.name}</span>
          )}
          {task.estimated_cost > 0 && (
            <span>💰 ₹{task.estimated_cost}</span>
          )}
        </div>

        {/* Status Badge */}
        <div style={{
          marginTop: '10px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span style={{
            padding: '5px 12px',
            borderRadius: '15px',
            background: getStatusColor(task.status),
            color: 'white',
            fontSize: '12px',
            fontWeight: '600'
          }}>
            {task.status?.replace('_', ' ').toUpperCase()}
          </span>
          <span style={{
            fontSize: '20px',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.3s'
          }}>
            ⌄
          </span>
        </div>
      </div>

      {/* Expanded Actions - Mobile Optimized */}
      {isExpanded && (
        <div style={{
          padding: '15px',
          borderTop: '1px solid #e0e0e0',
          background: '#f8f9fa'
        }}>
          {/* Additional Details */}
          <div style={{
            marginBottom: '15px',
            fontSize: '14px',
            color: '#666'
          }}>
            {task.notes && (
              <p style={{ margin: '0 0 10px 0' }}>
                <strong>Notes:</strong> {task.notes}
              </p>
            )}
            {task.assigned_person && (
              <p style={{ margin: '0 0 10px 0' }}>
                <strong>Assigned to:</strong> {task.assigned_person.name}
              </p>
            )}
            {task.created_by_user && (
              <p style={{ margin: '0' }}>
                <strong>Created by:</strong> {task.created_by_user.name}
              </p>
            )}
          </div>

          {/* Action Buttons - Mobile Optimized */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '10px'
          }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onTaskClick(task);
              }}
              style={{
                padding: '10px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              📋 View Details
            </button>

            {isAdmin && task.status === 'scheduled' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onQuickStatusUpdate(task.id, 'in_progress');
                }}
                style={{
                  padding: '10px',
                  background: '#17a2b8',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                ▶️ Start
              </button>
            )}

            {isAdmin && task.status === 'in_progress' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onTaskClick(task);
                }}
                style={{
                  padding: '10px',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                ✅ Complete
              </button>
            )}

            {task.service_vendors?.phone && (
              <a
                href={`tel:${task.service_vendors.phone}`}
                style={{
                  padding: '10px',
                  background: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                📞 Call Vendor
              </a>
            )}

            {isSuperAdmin && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(task.id);
                }}
                style={{
                  padding: '10px',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                🗑️ Delete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const selectStyle = {
  width: '100%',
  padding: '10px',
  border: '2px solid #e0e0e0',
  borderRadius: '8px',
  background: 'white',
  fontSize: '14px',
  cursor: 'pointer',
  outline: 'none'
};

export default MaintenanceTaskList;